import { useEffect, useRef, useState } from 'react';
import { useReveal } from '../hooks/useReveal.js';
import {
  CrossIcon,
  BookIcon,
  RopeIcon,
  WheatIcon,
  ScrollIcon,
  CenserIcon,
  QuillIcon,
  ProfileIcon,
  BellIcon,
  ChurchIcon,
  CandleIcon,
  KeyIcon,
} from '../components/icons.js';

type Module = {
  number: string;
  Icon: React.ComponentType<{ size?: number; className?: string }>;
  title: string;
  oneLiner: string;
  detail: string;
};

const modules: Module[] = [
  {
    number: 'I',
    Icon: CrossIcon,
    title: 'Liturgical Engine',
    oneLiner: 'The heart. Pascha, feasts, tones, fasting, computed for every day.',
    detail:
      'A pure-function engine that, given a date, resolves the Paschal cycle, the eight tones, the four fasts, and the day’s commemoration. Runs offline, on device.',
  },
  {
    number: 'II',
    Icon: BookIcon,
    title: 'Prayer System',
    oneLiner: 'Morning, evening, pre-communion, post-communion. Custom rules, reverent UI.',
    detail:
      'A complete library of canonical prayers, organised by rule. Build a custom rule with the prayers your spiritual father has given you.',
  },
  {
    number: 'III',
    Icon: RopeIcon,
    title: 'Jesus Prayer',
    oneLiner: 'A digital rope. Tap or breathe through 33, 50, or 100 knots.',
    detail:
      'A discreet rope on the screen. Tap to count, or use the breath-pacer. Your numbers are private and never gamified.',
  },
  {
    number: 'IV',
    Icon: WheatIcon,
    title: 'Fasting',
    oneLiner: 'Today’s rule, at your level. Beginner, standard, strict. Without guilt.',
    detail:
      'The rule for today, expressed visually. Three tiers, all canonical. The app never shames a meal, it only tells you what the day is.',
  },
  {
    number: 'V',
    Icon: ScrollIcon,
    title: 'Scripture & Readings',
    oneLiner: 'The day’s Epistle and Gospel. The whole Bible, navigable.',
    detail:
      'Daily readings resolved by the lectionary. The full Orthodox Study Bible, with cross-references to the services that quote it.',
  },
  {
    number: 'VI',
    Icon: CenserIcon,
    title: 'Service Companion',
    oneLiner: 'Vespers, Matins, Liturgy. Simple mode for the pew, full mode for the chanter.',
    detail:
      'Service texts assembled for today’s variables. Toggle between a clean follow-along view and the chanter’s full text with rubrics.',
  },
  {
    number: 'VII',
    Icon: QuillIcon,
    title: 'Learning',
    oneLiner: 'Catechesis without academic overload. Short, careful articles.',
    detail:
      'For inquirers, catechumens, and the simply curious. Each article is short, sourced, and approved by clergy.',
  },
  {
    number: 'VIII',
    Icon: ProfileIcon,
    title: 'You',
    oneLiner: 'Your name. Your patron. Your level. Your parish.',
    detail:
      'A small profile that holds your patron saint, your jurisdiction, your fasting level, and your home parish. That is all.',
  },
  {
    number: 'IX',
    Icon: BellIcon,
    title: 'Notifications',
    oneLiner: 'A reminder for morning prayer. A note before a feast. Nothing more.',
    detail:
      'Reverent notifications you can shape. No streaks, no nudges, no marketing. The app interrupts only when it should.',
  },
  {
    number: 'X',
    Icon: ChurchIcon,
    title: 'Parish',
    oneLiner: 'Service times, announcements, events. Find your community.',
    detail:
      'A directory of canonical Orthodox parishes. For your own parish, service times, the bulletin, and upcoming events.',
  },
  {
    number: 'XI',
    Icon: CandleIcon,
    title: 'Reflection',
    oneLiner: 'A quiet ledger. Notes encrypted. Nothing compared, nothing shared.',
    detail:
      'A private record of your prayer rule kept and your evening line. Encrypted at rest. Never shared, never compared.',
  },
  {
    number: 'XII',
    Icon: KeyIcon,
    title: 'Admin',
    oneLiner: 'For parishes and editors. Manage liturgical data, articles, announcements.',
    detail:
      'A separate admin surface for parish editors and content stewards. Granular roles, full audit log, no surprises.',
  },
];

function Card({ m, onOpen }: { m: Module; onOpen: () => void }) {
  return (
    <button
      type="button"
      onClick={onOpen}
      aria-label={`Read more about ${m.title}`}
      className="ow-lift-card text-left bg-bg-elevated border border-border flex flex-col w-full h-full"
      style={{
        borderRadius: 16,
        padding: '28px 28px 30px',
        minHeight: 280,
      }}
    >
      <span
        className="font-display text-gold self-start"
        style={{ fontSize: 11, letterSpacing: '0.32em' }}
      >
        {m.number}
      </span>

      <div className="flex justify-center my-6 text-gold ow-tilt-icon" aria-hidden>
        <m.Icon size={56} />
      </div>

      <div className="flex items-center justify-center gap-2 mb-4" aria-hidden>
        <span className="h-px w-6 bg-gold opacity-50" />
        <span className="text-gold" style={{ fontSize: 9 }}>
          ✦
        </span>
        <span className="h-px w-6 bg-gold opacity-50" />
      </div>

      <h3
        className="font-display text-primary"
        style={{ fontSize: 21, fontWeight: 500, letterSpacing: '0.02em', lineHeight: 1.2 }}
      >
        {m.title}
      </h3>
      <p className="text-secondary mt-2" style={{ fontSize: 14, lineHeight: 1.6 }}>
        {m.oneLiner}
      </p>
    </button>
  );
}

function Modal({ m, onClose }: { m: Module; onClose: () => void }) {
  const closeRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    closeRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-end md:items-center justify-center"
      role="dialog"
      aria-modal="true"
      aria-labelledby="module-modal-title"
    >
      <div
        className="absolute inset-0 backdrop-blur-xs"
        style={{ background: 'color-mix(in srgb, var(--ow-bg) 70%, transparent)' }}
        onClick={onClose}
      />
      <div
        className="relative bg-bg-elevated border border-border-strong rounded-card max-w-[560px] w-full mx-4 mb-4 md:mb-0"
        style={{
          padding: 36,
          boxShadow: 'var(--ow-shadow-lg)',
          animation: 'ow-fade-up 400ms var(--ow-ease-soft) both',
        }}
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="font-display text-gold" style={{ fontSize: 11, letterSpacing: '0.32em' }}>
              Module {m.number}
            </p>
            <h3
              id="module-modal-title"
              className="font-display text-primary mt-2"
              style={{ fontSize: 28, fontWeight: 500, letterSpacing: '0.02em' }}
            >
              {m.title}
            </h3>
          </div>
          <span className="text-gold shrink-0">
            <m.Icon size={48} />
          </span>
        </div>
        <p className="scripture text-secondary mt-4" style={{ fontSize: 19, lineHeight: 1.55 }}>
          {m.oneLiner}
        </p>
        <p className="text-secondary mt-4" style={{ fontSize: 15, lineHeight: 1.7 }}>
          {m.detail}
        </p>
        <div className="flex justify-end mt-6">
          <button ref={closeRef} type="button" onClick={onClose} className="ow-cta-ghost">
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

export function ModulesGrid() {
  const headerRef = useReveal<HTMLDivElement>(0.3);
  const gridRef = useReveal<HTMLDivElement>(0.15);
  const [active, setActive] = useState<Module | null>(null);

  return (
    <section id="modules" className="relative w-full" style={{ padding: '76px 20px' }}>
      <div className="mx-auto max-w-container">
        <div ref={headerRef} className="ow-reveal text-center max-w-[760px] mx-auto">
          <p
            className="font-display uppercase text-gold"
            style={{ fontSize: 11, letterSpacing: '0.32em' }}
          >
            § III - The Twelve
          </p>
          <h2
            className="font-display text-primary mt-5"
            style={{
              fontSize: 'clamp(32px, 5vw, 56px)',
              fontWeight: 500,
              letterSpacing: '0.02em',
            }}
          >
            Twelve modules. One way.
          </h2>
          <p className="scripture text-secondary mt-4" style={{ fontSize: 22, lineHeight: 1.5 }}>
            Each module is small. Together, they hold a life.
          </p>
        </div>

        <div
          ref={gridRef}
          className="ow-reveal mt-16 grid gap-6"
          style={{
            gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
          }}
        >
          {modules.map((m) => (
            <Card key={m.title} m={m} onOpen={() => setActive(m)} />
          ))}
        </div>
      </div>

      {active && <Modal m={active} onClose={() => setActive(null)} />}
    </section>
  );
}
